import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, color, font } from '@/design-system';
import { hostRole } from '../config/domains';
import { policiesApi } from '../services/policiesApi';
import {
  COOKIE_CONSENT_CHANGED,
  OPEN_COOKIE_SETTINGS,
  isCookieBannerPath,
  needsCookieAnswer,
  readCookieConsent,
  writeCookieConsent,
  type CookieChoice,
} from '../utils/cookieConsent';
import { cloudflareAnalyticsLoadedByUs, loadCloudflareAnalytics } from '../utils/cloudflareAnalytics';

// ---------------------------------------------------------------------------
// CookieConsentBanner — the banner the published Cookie Policy promises (B98).
//
// Policy §3: "When you first visit, a consent banner lets you accept or decline
// these categories, and you can change your choice at any time through the
// cookie settings link." Until B98 neither existed.
//
// Rulings (founder, 2026-10-10): Accept / Decline of equal weight, reopened by
// a "Cookie settings" link (the marketing footer, the signed-in Privacy page);
// on ReCurr's own pages only — never where the visitor deals with a merchant;
// the answer kept in the browser (utils/cookieConsent). What it governs is
// what was measured, and the words below say exactly that: Cloudflare Web
// Analytics, and the dashboard remembering dismissed notices.
//
// Not to be confused with components/ConsentBanner — the signed-in POPIA
// consent to the Terms and Privacy Policy, recorded on the server.
//
// ⚠ Public-site CSS must work on Safari 13.4+ (the founder's iPad is iPadOS 15,
// CLAUDE.md): the layout in index.css uses margins, not flex `gap`, and no
// container queries.
// ---------------------------------------------------------------------------

/** localhost and loopback count as ReCurr's own (dev, the dist preview); a merchant's own hostname does not. */
function isReCurrHost(): boolean {
  if (hostRole() !== 'unified') return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

let livePolicyVersion: Promise<number | null> | null = null;
function fetchLivePolicyVersion(): Promise<number | null> {
  if (!livePolicyVersion) {
    livePolicyVersion = policiesApi
      .effective('cookie')
      .then((r) => {
        const v = r.data?.data?.policy?.version;
        return typeof v === 'number' ? v : null;
      })
      .catch(() => null);
  }
  return livePolicyVersion;
}

const CookieConsentBanner: React.FC = () => {
  const { pathname, search } = useLocation();
  const inScope = isReCurrHost() && isCookieBannerPath(pathname, search);

  const [consent, setConsent] = useState(readCookieConsent);
  const [liveVersion, setLiveVersion] = useState<number | null>(null);
  const [reopened, setReopened] = useState(false);
  const firstButton = useRef<HTMLButtonElement>(null);
  const region = useRef<HTMLDivElement>(null);

  // Analytics runs only on an Accept, and only on ReCurr's own pages.
  useEffect(() => {
    if (inScope && consent?.choice === 'accepted') loadCloudflareAnalytics();
  }, [inScope, consent]);

  useEffect(() => {
    if (!inScope) return;
    let live = true;
    fetchLivePolicyVersion().then((v) => { if (live) setLiveVersion(v); });
    return () => { live = false; };
  }, [inScope]);

  useEffect(() => {
    const onOpen = () => setReopened(true);
    const onChanged = () => setConsent(readCookieConsent());
    window.addEventListener(OPEN_COOKIE_SETTINGS, onOpen);
    window.addEventListener(COOKIE_CONSENT_CHANGED, onChanged);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS, onOpen);
      window.removeEventListener(COOKIE_CONSENT_CHANGED, onChanged);
    };
  }, []);

  const visible = reopened || (inScope && needsCookieAnswer(consent, liveVersion));

  // Reopened from a settings link: put the keyboard on the choice.
  useEffect(() => {
    if (reopened) firstButton.current?.focus();
  }, [reopened]);

  // The page keeps room under its last line for the banner, so the footer —
  // and its "Cookie settings" link — can always be scrolled clear of it.
  useLayoutEffect(() => {
    if (!visible || !region.current) return;
    const el = region.current;
    const apply = () => { document.body.style.paddingBottom = `${el.offsetHeight}px`; };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const answer = useCallback(async (choice: CookieChoice) => {
    const hadOurBeacon = cloudflareAnalyticsLoadedByUs();
    const version = liveVersion ?? (await Promise.race([
      fetchLivePolicyVersion(),
      new Promise<null>((r) => setTimeout(() => r(null), 1500)),
    ]));
    const next = writeCookieConsent(choice, version);
    setConsent(next);
    setReopened(false);
    if (choice === 'accepted' && inScope) loadCloudflareAnalytics();
    // A script cannot be unloaded: a Decline after an Accept reloads the page without it.
    if (choice === 'declined' && hadOurBeacon) window.location.reload();
  }, [liveVersion, inScope]);

  if (!visible) return null;

  const decided = consent && consent.decidedAt
    ? new Date(consent.decidedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div
      ref={region}
      className="cookie-banner"
      role="region"
      aria-label="Cookie choices"
      data-testid="cookie-banner"
      style={{ background: color.bg.card, color: color.text.primary, borderTop: `1px solid ${color.border.strong}` }}
    >
      <div className="cookie-banner__inner">
        <div className="cookie-banner__text" style={{ fontSize: font.size.sm }}>
          {/* Two wordings of the same three facts: on a phone the long one made the
              banner tall enough to cover the home page's hero buttons (measured). */}
          <span className="cookie-banner__long">
            <strong style={{ display: 'block', fontSize: font.size.base, marginBottom: 4 }}>Cookies on ReCurr</strong>
            The cookies that keep you signed in and the site secure are always on. With your consent we also count
            page visits with Cloudflare Web Analytics (it sets no cookies) and remember notices you dismiss in your
            dashboard.{' '}
          </span>
          <span className="cookie-banner__short">
            <strong>Cookies.</strong> The necessary ones are always on. May we also count visits (Cloudflare Web
            Analytics, no cookies) and remember notices you dismiss?{' '}
          </span>
          <Link to="/legal/cookie" style={{ color: color.text.link }}>Cookie Policy</Link>
          {consent && decided && (
            <span style={{ display: 'block', marginTop: 4, color: color.text.secondary }} data-testid="cookie-banner-current">
              Your current choice: {consent.choice === 'accepted' ? 'accepted' : 'declined'} on {decided}.
            </span>
          )}
        </div>
        <div className="cookie-banner__actions">
          <Button ref={firstButton} variant="primary" onClick={() => answer('accepted')}>Accept</Button>
          <Button variant="primary" onClick={() => answer('declined')}>Decline</Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
